import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';
import Navbar from './components/Navbar.jsx';
import SAMLLogin from './components/auth/SAMLLogin.jsx';
import ManualLogin from './components/auth/ManualLogin.jsx';
import ProtectedRoute from './components/auth/ProtectedRoute.jsx';
import { AUTH_CONFIG } from './config';
import Footer from './Footer.jsx';
import ErrorBoundary from './ErrorBoundary.jsx';

const Dashboard = React.lazy(() => import('./Dashboard.jsx'));
const UserProfile = React.lazy(() => import('./components/auth/UserProfile.jsx'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard.jsx'));
const UserManagement = React.lazy(() => import('./components/admin/UserManagement.jsx'));
const SAMLConfiguration = React.lazy(() => import('./components/admin/SAMLConfiguration.jsx'));
const ApplicationsManagement = React.lazy(() => import('./components/admin/ApplicationsManagement.jsx'));
const DepartmentsManagement = React.lazy(() => import('./components/admin/DepartmentsManagement.jsx'));
const CloudAccountsManagement = React.lazy(() => import('./components/admin/CloudAccountsManagement.jsx'));
const IaCStateSources = React.lazy(() => import('./components/admin/IaCStateSources.jsx'));
const CloudServicesAdmin = React.lazy(() => import('./components/admin/CloudServicesAdmin.jsx'));
const BudgetManagement = React.lazy(() => import('./components/admin/BudgetManagement.jsx'));
const AIPage = React.lazy(() => import('./components/AIPage.jsx'));
const CostComparison = React.lazy(() => import('./CostComparison.jsx'));
const RightSizingPage = React.lazy(() => import('./components/RightSizingPage.jsx'));
const CloudExplorer = React.lazy(() => import('./components/CloudExplorer.jsx'));
const CostsHub = React.lazy(() => import('./components/costs/CostsHub.jsx'));
const InventoryPage = React.lazy(() => import('./components/InventoryPage.jsx'));
const RetentionSettings = React.lazy(() => import('./components/admin/RetentionSettings.jsx'));
const BrandingManagement = React.lazy(() => import('./components/admin/BrandingManagement.jsx'));
const ForecastPage = React.lazy(() => import('./components/ForecastPage.jsx'));
const AnomaliesPage = React.lazy(() => import('./components/costs/AnomaliesPage.jsx'));

import { DepartmentProvider } from './contexts/DepartmentContext.jsx';
import { BrandingProvider } from './contexts/BrandingContext.jsx';
import CommandPalette from './components/CommandPalette.jsx';

// Loading component
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
    <div className="spinner-border" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Wrap routes with AuthProvider
function AppRoutes() {
  const auth = useAuth();
  const currentUser = auth?.currentUser;
  const loading = auth?.loading || false;
  const navigate = useNavigate();

  const handleAdminClick = () => {
    navigate('/manual-login');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <BrandingProvider>
      <DepartmentProvider>
        <ErrorBoundary>
          <Navbar />
        <CommandPalette />
        <Box sx={{ width: '100%' }}>
        <React.Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={
              currentUser ? (
                <ErrorBoundary>
                  <Dashboard />
                </ErrorBoundary>
              ) : (
                <ErrorBoundary>
                  <SAMLLogin />
                </ErrorBoundary>
              )
            } />

            <Route path={AUTH_CONFIG.manualLoginPath} element={
              <ErrorBoundary>
                <ManualLogin />
              </ErrorBoundary>
            } />

            {/* Protected routes */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <UserProfile />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            {/* Old /applications page now lives as a tab inside /costs.
                Redirect any deep-link or bookmark to the new home. */}
            <Route path="/applications" element={<Navigate to="/costs/applications" replace />} />
            <Route path="/programs" element={<Navigate to="/costs/applications" replace />} />

            <Route path="/cloud-accounts" element={<Navigate to="/costs/providers" replace />} />

            {/* Reports lives inside Costs > Cloud Providers (CostsHub
                embeds the same ReportsList component). The standalone
                /reports route was a duplicate navbar entry — redirected
                here so old links + AdminDashboard tile keep resolving. */}
            <Route path="/reports" element={<Navigate to="/costs/providers" replace />} />

            {/* New Costs hub — /costs/providers, /costs/services/llm, /costs/services/m365.
                The hub itself owns the sub-routing (see components/costs/CostsHub.jsx). */}
            <Route path="/costs/*" element={
              <ProtectedRoute requiredAccess="financials">
                <ErrorBoundary>
                  <CostsHub />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/inventory" element={
              <ProtectedRoute requiredAccess="inventory">
                <ErrorBoundary>
                  <InventoryPage />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/explorer" element={
              <ProtectedRoute requiredAccess="financials">
                <ErrorBoundary>
                  <CloudExplorer />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            {/* Admin routes */}
            <Route path="/admin" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary>
                  <AdminDashboard />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/admin/applications" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary><ApplicationsManagement /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/admin/departments" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary><DepartmentsManagement /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/admin/programs" element={<Navigate to="/admin/applications" replace />} />

            <Route path="/admin/cloud-accounts" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary>
                  <CloudAccountsManagement />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/admin/iac-state" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary>
                  <IaCStateSources />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            {/* Unified Cloud Services admin — every third-party SaaS
                (LLM providers, M365, manual SaaS tracker) lives here as a
                tab. The old /admin/llm-providers and /admin/m365-tenants
                routes redirect to the right tab so deep-links keep working. */}
            <Route path="/admin/cloud-services" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary>
                  <CloudServicesAdmin />
                </ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/admin/llm-providers" element={<Navigate to="/admin/cloud-services?tab=llm" replace />} />
            <Route path="/admin/m365-tenants" element={<Navigate to="/admin/cloud-services?tab=m365" replace />} />

            <Route path="/admin/retention" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary>
                  <RetentionSettings />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/admin/budgets" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary>
                  <BudgetManagement />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/admin/branding" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.ADMIN}>
                <ErrorBoundary>
                  <BrandingManagement />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            {/* Cost and optimization analysis requires financial access. */}
            <Route path="/ai" element={
              <ProtectedRoute requiredAccess="financials">
                <ErrorBoundary><AIPage /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/comparison" element={
              <ProtectedRoute requiredAccess="financials">
                <ErrorBoundary><CostComparison /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/rightsizing" element={
              <ProtectedRoute requiredAccess="financials">
                <ErrorBoundary><RightSizingPage /></ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/forecast" element={
              <ProtectedRoute requiredAccess="financials">
                <ErrorBoundary><ForecastPage /></ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/anomalies" element={
              <ProtectedRoute requiredAccess="financials">
                <ErrorBoundary><AnomaliesPage /></ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/admin/ai-insights" element={<Navigate to="/ai" replace />} />

            {/* System Admin routes */}
            <Route path="/admin/users" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.SUPER_ADMIN}>
                <ErrorBoundary>
                  <UserManagement />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            <Route path="/admin/saml" element={
              <ProtectedRoute requiredRole={AUTH_CONFIG.roles.SUPER_ADMIN}>
                <ErrorBoundary>
                  <SAMLConfiguration />
                </ErrorBoundary>
              </ProtectedRoute>
            } />

            {/* SAML callback route */}
            <Route path="/auth/saml/callback" element={
              <ErrorBoundary>
                <div className="container mt-5">
                  <div className="alert alert-info">
                    Processing SAML response... Please wait.
                  </div>
                </div>
              </ErrorBoundary>
            } />

            {/* Fallback route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </React.Suspense>
      </Box>
      <Footer onAdminClick={handleAdminClick} />
        </ErrorBoundary>
      </DepartmentProvider>
    </BrandingProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
